"use client";

import { usePath, useSearch } from "@packages/framework";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";

export type UseDebouncedSearchParamStateProps<
  OptionsMultipleType extends boolean = false,
  ValueType extends string = string,
> = {
  defaultValue?: OptionsMultipleType extends true ? ValueType[] : ValueType;
  key: string;
  multiple?: OptionsMultipleType;
  removeFromUrlIfDefaultValue?: boolean;
  delay?: number;
};

export const useDebouncedSearchParamState = <
  OptionsMultipleType extends boolean = false,
  ValueType extends string = string,
>({
  defaultValue,
  key,
  multiple,
  removeFromUrlIfDefaultValue,
  delay = 200,
}: UseDebouncedSearchParamStateProps<OptionsMultipleType>) => {
  const path = usePath();
  const search = useSearch();
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const [state, setState] = useState<
    UseDebouncedSearchParamStateProps<OptionsMultipleType>["defaultValue"]
  >(
    (multiple
      ? search.getAll(key)?.length > 0
        ? search.getAll(key)
        : (defaultValue ?? [])
      : search.get(key)?.length > 0
        ? search.get(key)
        : (defaultValue ??
          "")) as UseDebouncedSearchParamStateProps<OptionsMultipleType>["defaultValue"],
  );

  const [debouncedState, setDebouncedState] =
    useState<
      UseDebouncedSearchParamStateProps<OptionsMultipleType>["defaultValue"]
    >(state);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setDebouncedState(state);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state, delay]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(search.toString());

    const old = multiple
      ? newSearchParams.getAll(key)
      : newSearchParams.get(key);
    const replace = !old && !removeFromUrlIfDefaultValue;

    // update search params IF either there was none and we want to show it,
    // OR there was none and debouncedState is no longer the default value,
    // OR there was one but it is different from what debouncedState is now
    const update =
      (!old &&
        (!removeFromUrlIfDefaultValue || debouncedState !== defaultValue)) ||
      JSON.stringify(old) !== JSON.stringify(debouncedState);

    if (update) {
      newSearchParams.delete(key);

      if (
        !(
          removeFromUrlIfDefaultValue &&
          JSON.stringify(defaultValue) === JSON.stringify(debouncedState)
        )
      ) {
        if (multiple && debouncedState?.length !== 0)
          (debouncedState as string[]).forEach((stateValue) => {
            if (stateValue?.length > 0) newSearchParams.append(key, stateValue);
          });
        else if (debouncedState?.length > 0)
          newSearchParams.set(key, debouncedState as string);
      }

      newSearchParams.sort();

      const newPath = `${path}${newSearchParams.toString() ? "?" + newSearchParams.toString() : ""}`;
      if (typeof history !== "undefined") {
        const url = new URL(newPath, window.location.href);
        if (replace) {
          history.replaceState(null, "", url);
        } else {
          history.pushState(null, "", url);
        }
      }
    }
  }, [debouncedState, search]);

  useEffect(() => {
    const paramValue = multiple
      ? search.getAll(key)?.length > 0
        ? search.getAll(key)
        : (defaultValue ?? [])
      : search.get(key)?.length > 0
        ? search.get(key)
        : (defaultValue ?? "");

    if (JSON.stringify(paramValue) !== JSON.stringify(state)) {
      setState(
        paramValue as UseDebouncedSearchParamStateProps<OptionsMultipleType>["defaultValue"],
      );
    }
  }, [search]);

  return [state, setState, debouncedState] as [
    UseDebouncedSearchParamStateProps<
      OptionsMultipleType,
      ValueType
    >["defaultValue"],
    Dispatch<
      SetStateAction<
        UseDebouncedSearchParamStateProps<
          OptionsMultipleType,
          ValueType
        >["defaultValue"]
      >
    >,
    UseDebouncedSearchParamStateProps<
      OptionsMultipleType,
      ValueType
    >["defaultValue"],
  ];
};
